// src/tareas/tableroTareasDepartamento.jsx
import React, { useEffect, useState, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Global } from "../helpers/Global";

const ESTADOS = [
  { id: "pendiente", titulo: "Pendiente" },
  { id: "en_curso", titulo: "En curso" },
  { id: "resuelta", titulo: "Resuelta" },
  { id: "rechazada", titulo: "Rechazada" },
];

const authHeadersJson = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const Toast = ({ type = "info", children }) => {
  if (!children) return null;
  const base = {
    position: "fixed",
    top: 80,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 9999,
    padding: "10px 16px",
    borderRadius: 8,
    fontWeight: 600,
    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
    maxWidth: "90vw",
    textAlign: "center",
  };
  const variants = {
    ok: {
      background: "#e7f8ed",
      color: "#0f5132",
      border: "1px solid #b6f0c6",
    },
    error: {
      background: "#fdeceb",
      color: "#842029",
      border: "1px solid #f5c2c7",
    },
    info: {
      background: "#e8f6ff",
      color: "#084298",
      border: "1px solid #b6d9ff",
    },
  };
  return <div style={{ ...base, ...(variants[type] || {}) }}>{children}</div>;
};

// ---- Avatares ----
const getAvatarUrl = (usuario) => {
  if (!usuario?.avatar_url) return null;
  return `${Global.url}${usuario.avatar_url}`;
};

const getIniciales = (usuario) => {
  const nombre = (usuario?.nombre || "").trim();
  const apellidos = (usuario?.apellidos || "").trim();
  const n1 = nombre ? nombre.split(" ")[0][0] : "";
  const a1 = apellidos ? apellidos.split(" ")[0][0] : "";
  const combo = (n1 + a1).toUpperCase();
  if (combo) return combo;
  if (nombre) return nombre[0].toUpperCase();
  return "?";
};

// ---- Fechas ----
const formatearFechaCompleta = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const infoFechaVencimiento = (iso) => {
  if (!iso) return { texto: "", clase: "" };

  const fin = new Date(iso);
  const hoy = new Date();
  const msPorDia = 1000 * 60 * 60 * 24;
  const diff = Math.floor((fin - hoy) / msPorDia);

  if (Number.isNaN(diff)) return { texto: iso, clase: "" };

  if (diff < 0) {
    const dias = Math.abs(diff);
    const texto =
      dias === 0
        ? "Vencida"
        : `Vencida hace ${dias} día${dias === 1 ? "" : "s"}`;
    return { texto, clase: "badge-fecha--critica" };
  }
  if (diff === 0) return { texto: "Vence hoy", clase: "badge-fecha--warning" };
  if (diff === 1) return { texto: "Vence mañana", clase: "badge-fecha--warning" };
  if (diff <= 7)
    return { texto: `Vence en ${diff} días`, clase: "badge-fecha--warning" };

  return { texto: `Vence en ${diff} días`, clase: "badge-fecha--ok" };
};

export const TableroTareasDepartamento = () => {
  const [departamentos, setDepartamentos] = useState([]);
  const [deptoSeleccionado, setDeptoSeleccionado] = useState(null);

  const [tareasPorEstado, setTareasPorEstado] = useState({});
  const [cargandoDeptos, setCargandoDeptos] = useState(true);
  const [cargandoTablero, setCargandoTablero] = useState(false);

  const [toast, setToast] = useState(null);
  const [usuariosMapa, setUsuariosMapa] = useState({});

  // Usuarios del departamento (para reasignar)
  const [usuariosDepto, setUsuariosDepto] = useState([]);
  const [cargandoUsuariosDepto, setCargandoUsuariosDepto] = useState(false);

  // Modal historial
  const [histModalAbierto, setHistModalAbierto] = useState(false);
  const [histCargando, setHistCargando] = useState(false);
  const [histEventos, setHistEventos] = useState([]);
  const [peticionSeleccionada, setPeticionSeleccionada] = useState(null);

  // Panel de gestión (modal "rueda dentada")
  const [panelAbierto, setPanelAbierto] = useState(false);
  const [peticionPanel, setPeticionPanel] = useState(null);
  const [selectedUsuarioId, setSelectedUsuarioId] = useState("");
  const [nuevoEstado, setNuevoEstado] = useState("");
  const [comentarioAccion, setComentarioAccion] = useState("");
  const [guardandoPanel, setGuardandoPanel] = useState(false);

  const mostrarToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  const inicializarEstado = (items) => {
    const base = {};
    ESTADOS.forEach((e) => (base[e.id] = []));
    (items || []).forEach((item) => {
      const estado = ESTADOS.some((e) => e.id === item.estado)
        ? item.estado
        : "pendiente";
      base[estado].push(item);
    });
    setTareasPorEstado(base);
  };

  // Cargar SOLO los departamentos del usuario autenticado
  useEffect(() => {
    const fetchDeptos = async () => {
      try {
        const res = await fetch(`${Global.url}departamentos/mis`, {
          headers: authHeaders(),
        });
        const data = await res.json().catch(() => []);
        if (res.ok && Array.isArray(data)) {
          const activos = data.filter((d) => d.activo !== false);
          setDepartamentos(activos);
          if (activos.length === 1) {
            setDeptoSeleccionado(activos[0].id);
          }
        } else {
          console.error("Error cargando mis departamentos:", data);
        }
      } catch (e) {
        console.error("Error cargando mis departamentos:", e);
      } finally {
        setCargandoDeptos(false);
      }
    };
    fetchDeptos();
  }, []);

  // Cargar usuarios (para avatares y nombres de creadores/asignatarios)
  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const res = await fetch(`${Global.url}usuarios/todos`, {
          headers: authHeaders(),
        });
        const data = await res.json().catch(() => []);
        if (res.ok && Array.isArray(data)) {
          const mapa = {};
          data.forEach((u) => {
            mapa[u.id] = u;
          });
          setUsuariosMapa(mapa);
        }
      } catch (e) {
        console.error("Error cargando usuarios para avatares:", e);
      }
    };
    fetchUsuarios();
  }, []);

  // Cargar usuarios del departamento para reasignación
  useEffect(() => {
    const fetchUsuariosDepto = async () => {
      if (!deptoSeleccionado) {
        setUsuariosDepto([]);
        return;
      }
      setCargandoUsuariosDepto(true);
      try {
        const res = await fetch(
          `${Global.url}departamentos/${deptoSeleccionado}/usuarios`,
          { headers: authHeaders() }
        );
        const data = await res.json().catch(() => []);
        if (res.ok && Array.isArray(data)) {
          const activos = data.filter((u) => u.miembro_activo !== false);
          setUsuariosDepto(activos);
        } else {
          console.error("Error cargando usuarios del departamento:", data);
          setUsuariosDepto([]);
        }
      } catch (e) {
        console.error("Error cargando usuarios del departamento:", e);
        setUsuariosDepto([]);
      } finally {
        setCargandoUsuariosDepto(false);
      }
    };

    fetchUsuariosDepto();
  }, [deptoSeleccionado]);

  // Función reutilizable para cargar el tablero del departamento
  const cargarTableroDepartamento = async (departamentoIdActual) => {
    if (!departamentoIdActual) {
      inicializarEstado([]);
      return;
    }

    setCargandoTablero(true);
    try {
      const endpoint = `${Global.url}tareas/tablero?scope=departamento&departamentoId=${departamentoIdActual}`;
      const res = await fetch(endpoint, { headers: authHeaders() });
      const data = await res.json().catch(() => []);
      if (!res.ok) {
        console.error("Error al cargar tablero de departamento:", data);
        mostrarToast(
          "error",
          data.message || "No se pudo cargar el tablero del departamento"
        );
        inicializarEstado([]);
        return;
      }
      inicializarEstado(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error al cargar tablero de departamento:", e);
      mostrarToast(
        "error",
        "Error de red al cargar el tablero del departamento"
      );
      inicializarEstado([]);
    } finally {
      setCargandoTablero(false);
    }
  };

  // Cargar tablero cuando cambia el departamento
  useEffect(() => {
    if (!deptoSeleccionado) {
      inicializarEstado([]);
      return;
    }
    cargarTableroDepartamento(deptoSeleccionado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptoSeleccionado]);

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const peticionId = Number(draggableId);
    const nuevoEstado = destination.droppableId;

    setTareasPorEstado((prev) => {
      const copy = {};
      ESTADOS.forEach((e) => (copy[e.id] = [...(prev[e.id] || [])]));

      const origenArr = copy[source.droppableId];
      const destinoArr = copy[destination.droppableId];

      const [movida] = origenArr.splice(source.index, 1);
      if (!movida) return prev;

      movida.estado = nuevoEstado;
      destinoArr.splice(destination.index, 0, movida);

      return copy;
    });

    try {
      const res = await fetch(
        `${Global.url}tareas/peticiones/${peticionId}/estado`,
        {
          method: "PATCH",
          headers: authHeadersJson(),
          body: JSON.stringify({ estado: nuevoEstado }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error("Error al actualizar estado:", data);
        mostrarToast(
          "error",
          data.message || "No se pudo actualizar el estado"
        );
        if (deptoSeleccionado) {
          cargarTableroDepartamento(deptoSeleccionado);
        }
      } else {
        mostrarToast("ok", "Estado actualizado");
      }
    } catch (e) {
      console.error("Error al actualizar estado:", e);
      mostrarToast("error", "Error de red al actualizar el estado");
      if (deptoSeleccionado) {
        cargarTableroDepartamento(deptoSeleccionado);
      }
    }
  };

  // ---------- Historial ----------
  const abrirHistorial = async (tareaPeticion) => {
    const peticionId = tareaPeticion.peticion_id;
    setPeticionSeleccionada(tareaPeticion);
    setHistEventos([]);
    setHistCargando(true);
    setHistModalAbierto(true);

    try {
      const res = await fetch(
        `${Global.url}peticiones/${peticionId}/historico`,
        { headers: authHeaders() }
      );
      const data = await res.json().catch(() => []);
      if (!res.ok) {
        console.error("Error al cargar historial:", data);
        mostrarToast(
          "error",
          data.message || "No se pudo cargar el historial"
        );
        setHistEventos([]);
        return;
      }
      setHistEventos(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error al cargar historial:", e);
      mostrarToast("error", "Error de red al cargar el historial");
      setHistEventos([]);
    } finally {
      setHistCargando(false);
    }
  };

  const cerrarHistorial = () => {
    setHistModalAbierto(false);
    setPeticionSeleccionada(null);
    setHistEventos([]);
    setHistCargando(false);
  };

  // ---------- Panel de gestión (rueda dentada) ----------
  const abrirPanelTarea = (tareaPeticion) => {
    setPeticionPanel(tareaPeticion || null);
    setPanelAbierto(true);

    const asigId = tareaPeticion?.asignado_a_usuario_id || null;
    setSelectedUsuarioId(asigId ? String(asigId) : "");
    setNuevoEstado(tareaPeticion?.estado || "pendiente");
    setComentarioAccion("");
  };

  const cerrarPanelTarea = () => {
    setPanelAbierto(false);
    setPeticionPanel(null);
    setSelectedUsuarioId("");
    setNuevoEstado("");
    setComentarioAccion("");
    setGuardandoPanel(false);
  };

  const guardarPanelTarea = async () => {
    if (!peticionPanel) return;
    const peticionId = peticionPanel.peticion_id;

    const asignPrev = peticionPanel.asignado_a_usuario_id || null;
    const asignNuevo = selectedUsuarioId ? Number(selectedUsuarioId) : null;

    const hayCambioAsignacion = asignNuevo !== asignPrev;
    const hayCambioEstado =
      nuevoEstado && nuevoEstado !== peticionPanel.estado;
    const hayComentario =
      typeof comentarioAccion === "string" &&
      comentarioAccion.trim().length > 0;

    if (!hayCambioAsignacion && !hayCambioEstado && !hayComentario) {
      mostrarToast("info", "No hay cambios que aplicar.");
      return;
    }

    setGuardandoPanel(true);

    try {
      // 1) Asignación y/o comentario
      if (hayCambioAsignacion || hayComentario) {
        const bodyAsignacion = {
          ...(hayCambioAsignacion && {
            asignado_a_usuario_id: asignNuevo,
          }),
          comentario: hayComentario ? comentarioAccion.trim() : undefined,
        };

        const resAsign = await fetch(
          `${Global.url}peticiones/${peticionId}`,
          {
            method: "PATCH",
            headers: authHeadersJson(),
            body: JSON.stringify(bodyAsignacion),
          }
        );
        const dataAsign = await resAsign.json().catch(() => ({}));
        if (!resAsign.ok) {
          console.error("Error al guardar asignación/comentario:", dataAsign);
          mostrarToast(
            "error",
            dataAsign.message ||
              "No se pudo guardar la asignación/comentario"
          );
          setGuardandoPanel(false);
          return;
        }
      }

      // 2) Cambio de estado
      if (hayCambioEstado) {
        const bodyEstado = {
          estado: nuevoEstado,
          comentario: hayComentario ? comentarioAccion.trim() : undefined,
        };

        const resEstado = await fetch(
          `${Global.url}tareas/peticiones/${peticionId}/estado`,
          {
            method: "PATCH",
            headers: authHeadersJson(),
            body: JSON.stringify(bodyEstado),
          }
        );
        const dataEstado = await resEstado.json().catch(() => ({}));
        if (!resEstado.ok) {
          console.error("Error al cambiar estado:", dataEstado);
          mostrarToast(
            "error",
            dataEstado.message || "No se pudo cambiar el estado"
          );
          setGuardandoPanel(false);
          return;
        }
      }

      mostrarToast("ok", "Cambios aplicados correctamente.");
      cerrarPanelTarea();
      if (deptoSeleccionado) {
        await cargarTableroDepartamento(deptoSeleccionado);
      }
    } catch (e) {
      console.error("Error guardando cambios en el panel:", e);
      mostrarToast("error", "Error de red al guardar los cambios.");
    } finally {
      setGuardandoPanel(false);
    }
  };

  const columnas = useMemo(() => ESTADOS, []);
  const nombreDeptoSeleccionado =
    departamentos.find((d) => d.id === deptoSeleccionado)?.nombre || "";

  return (
    <div className="kanban-wrapper">
      {toast && <Toast type={toast.type}>{toast.text}</Toast>}

      <h2 className="centrar-titulo">Tareas por departamento</h2>

      <div className="tareas-depto-filtros">
        <label htmlFor="select-departamento" className="tareas-depto-label">
          Departamento:
        </label>

        {cargandoDeptos ? (
          <span className="tareas-depto-loading">Cargando…</span>
        ) : departamentos.length === 0 ? (
          <span className="tareas-depto-empty">
            No perteneces a ningún departamento con tablero de tareas.
          </span>
        ) : (
          <select
            id="select-departamento"
            className="input-layout tareas-depto-select"
            value={deptoSeleccionado || ""}
            onChange={(e) =>
              setDeptoSeleccionado(
                e.target.value ? Number(e.target.value) : null
              )
            }
          >
            <option value="">-- Selecciona un departamento --</option>
            {departamentos.map((d) => (
              <option key={d.id} value={d.id}>
                {d.nombre}
              </option>
            ))}
          </select>
        )}
      </div>

      {!cargandoDeptos &&
        departamentos.length > 0 &&
        !deptoSeleccionado && (
          <p className="tareas-depto-hint">
            Elige un departamento para ver su tablero de tareas.
          </p>
        )}

      {deptoSeleccionado && (
        <>
          <h3 className="centrar-titulo">
            {nombreDeptoSeleccionado
              ? `Tareas del departamento ${nombreDeptoSeleccionado}`
              : "Tareas del departamento"}
          </h3>

          {cargandoTablero ? (
            <p>Cargando tablero…</p>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <div className="kanban-board">
                {columnas.map((col) => (
                  <div key={col.id} className="kanban-column">
                    <div className="kanban-column-header">
                      <span>{col.titulo}</span>
                      <span className="kanban-column-count">
                        {(tareasPorEstado[col.id] || []).length}
                      </span>
                    </div>

                    <Droppable droppableId={col.id}>
                      {(provided, snapshot) => (
                        <div
                          className={`kanban-column-body${
                            snapshot.isDraggingOver
                              ? " kanban-column-body--active"
                              : ""
                          }`}
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {(tareasPorEstado[col.id] || []).map(
                            (tarea, index) => {
                              const creador = usuariosMapa[tarea.creador_id];
                              const infoFecha = infoFechaVencimiento(
                                tarea.fecha_vencimiento
                              );

                              const responsableActual =
                                tarea.asignado_a_usuario_id &&
                                usuariosMapa[tarea.asignado_a_usuario_id]
                                  ? usuariosMapa[tarea.asignado_a_usuario_id]
                                  : null;

                              const nombreResponsableActual = responsableActual
                                ? `${responsableActual.nombre || ""} ${
                                    responsableActual.apellidos || ""
                                  }`.trim()
                                : null;

                              return (
                                <Draggable
                                  key={tarea.peticion_id}
                                  draggableId={String(tarea.peticion_id)}
                                  index={index}
                                >
                                  {(providedDraggable, snapshotDraggable) => (
                                    <div
                                      className={`kanban-card${
                                        snapshotDraggable.isDragging
                                          ? " kanban-card--dragging"
                                          : ""
                                      }`}
                                      ref={providedDraggable.innerRef}
                                      {...providedDraggable.draggableProps}
                                      {...providedDraggable.dragHandleProps}
                                    >
                                      {/* CABECERA: avatar + nombre en fila */}
                                      <div className="kanban-card-header">
                                        <div className="kanban-card-avatar">
                                          {creador && getAvatarUrl(creador) ? (
                                            <img
                                              src={getAvatarUrl(creador)}
                                              alt={`Avatar de ${
                                                creador.nombre || "usuario"
                                              }`}
                                            />
                                          ) : (
                                            <div className="avatar-fallback avatar-fallback--small">
                                              {getIniciales(creador || {})}
                                            </div>
                                          )}
                                        </div>
                                        <div className="kanban-card-header-text">
                                          <span className="kanban-card-creator-name">
                                            {creador
                                              ? `${creador.nombre || ""} ${
                                                  creador.apellidos || ""
                                                }`.trim()
                                              : "Creador desconocido"}
                                          </span>
                                          <span className="kanban-card-creator-label">
                                            Creador
                                          </span>
                                        </div>
                                      </div>

                                      {/* Título */}
                                      <div className="kanban-card-title">
                                        {tarea.titulo}
                                      </div>

                                      {/* Descripción */}
                                      {tarea.descripcion && (
                                        <div className="kanban-card-desc">
                                          {tarea.descripcion}
                                        </div>
                                      )}

                                      {/* Meta */}
                                      <div className="kanban-card-meta">
                                        <span
                                          className={`badge-prioridad badge-prioridad--${
                                            tarea.prioridad || "normal"
                                          }`}
                                        >
                                          {tarea.prioridad || "normal"}
                                        </span>

                                        {tarea.fecha_vencimiento &&
                                          infoFecha.texto && (
                                            <span
                                              className={`badge-fecha ${infoFecha.clase}`}
                                              title={formatearFechaCompleta(
                                                tarea.fecha_vencimiento
                                              )}
                                            >
                                              ⏰ {infoFecha.texto}
                                            </span>
                                          )}
                                      </div>

                                      {/* Destino / Asignación visible */}
                                      <div className="kanban-card-footer">
                                        {nombreResponsableActual ? (
                                          <span className="kanban-destino">
                                            Responsable:{" "}
                                            {nombreResponsableActual}
                                          </span>
                                        ) : (
                                          <span className="kanban-destino">
                                            Responsable: sin asignar
                                          </span>
                                        )}
                                      </div>

                                      <div className="kanban-card-actions">
                                        <button
                                          type="button"
                                          className="kanban-card-history-btn"
                                          onClick={() => abrirHistorial(tarea)}
                                        >
                                          Ver historial
                                        </button>
                                        <button
                                          type="button"
                                          className="kanban-card-config-btn"
                                          onClick={() => abrirPanelTarea(tarea)}
                                        >
                                          ⚙ Gestionar
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            }
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                ))}
              </div>
            </DragDropContext>
          )}
        </>
      )}

    {/* MODAL HISTORIAL */}
    {histModalAbierto && (
      <div className="modal-overlay modal-historial-overlay">
        <div className="modal-content modal-historial-contenido">

          {/* BOTÓN CERRAR */}
          <button className="modal-close historial-close-btn" onClick={cerrarHistorial}>
            ✕
          </button>

          <h3 className="centrar-titulo">Historial de petición #{peticionSeleccionada?.peticion_id}</h3>

          <p style={{ marginBottom: 16 }}>
            <strong>Tarea:</strong> {peticionSeleccionada?.titulo}
          </p>

          <div className="historial-scroll">

            {histCargando ? (
              <p>Cargando historial…</p>
            ) : histEventos.length === 0 ? (
              <p style={{ fontStyle: "italic" }}>No hay histórico registrado para esta petición.</p>
            ) : (
              histEventos.map((ev) => {
                const usuarioNombre = `${ev.modificado_por_nombre || ""} ${ev.modificado_por_apellidos || ""}`.trim();

                // ¿Hubo reasignación?
                const hayCambioAsignacion =
                  ev.asignado_anterior_id !== null || ev.asignado_nuevo_id !== null;

                const asignadoAnteriorNombre =
                  ev.asignado_anterior_nombre || ev.asignado_anterior_apellidos
                    ? `${ev.asignado_anterior_nombre || ""} ${ev.asignado_anterior_apellidos || ""}`.trim()
                    : "Sin responsable";

                const asignadoNuevoNombre =
                  ev.asignado_nuevo_nombre || ev.asignado_nuevo_apellidos
                    ? `${ev.asignado_nuevo_nombre || ""} ${ev.asignado_nuevo_apellidos || ""}`.trim()
                    : "Sin responsable";

                return (
                  <div key={ev.id} className="historial-item-card">

                    {/* Usuario + Fecha */}
                    <div className="historial-header">
                      <div className="avatar-small">{usuarioNombre.charAt(0)}</div>
                      <div className="historial-header-text">
                        <span className="historial-user">{usuarioNombre}</span>
                        <span className="historial-date">
                          {formatearFechaCompleta(ev.fecha_cambio)}
                        </span>
                      </div>
                    </div>

                    {/* ACCIÓN */}
                    <div className="historial-body">
                      {hayCambioAsignacion ? (
                        <span>
                          cambió la asignación de <strong>{asignadoAnteriorNombre}</strong> a{" "}
                          <strong>{asignadoNuevoNombre}</strong>.
                        </span>
                      ) : (
                        <span>
                          cambió el estado de <strong>{ev.estado_anterior}</strong> a{" "}
                          <strong>{ev.estado}</strong>.
                        </span>
                      )}

                      {ev.comentario && (
                        <div className="historial-comentario">
                          «{ev.comentario}»
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}

          </div>
        </div>
      </div>
    )}


      {/* MODAL PANEL DE GESTIÓN */}
      {panelAbierto && peticionPanel && (
        <div
          className="modal-overlay"
          style={{
            zIndex: 9999,
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            className="modal-content modal-config-tarea"
            style={{
              background: "#fff",
              borderRadius: 8,
              maxWidth: 700,
              width: "90%",
              padding: "20px 24px",
              position: "relative",
            }}
          >
            <button
              className="modal-close"
              onClick={cerrarPanelTarea}
              style={{
                position: "absolute",
                top: 10,
                right: 14,
                border: "none",
                background: "transparent",
                fontSize: 20,
                cursor: "pointer",
              }}
            >
              &times;
            </button>

            <h3 className="centrar-titulo">
              Gestionar petición #{peticionPanel.peticion_id}
            </h3>

            <p>
              <strong>Tarea:</strong> {peticionPanel.titulo}
            </p>
            <p>
              <strong>Estado actual:</strong> {peticionPanel.estado}
            </p>

            <p>
              <strong>Responsable actual:</strong>{" "}
              {peticionPanel.asignado_a_usuario_id &&
              usuariosMapa[peticionPanel.asignado_a_usuario_id]
                ? `${usuariosMapa[peticionPanel.asignado_a_usuario_id]
                    .nombre || ""} ${
                    usuariosMapa[peticionPanel.asignado_a_usuario_id]
                      .apellidos || ""
                  }`.trim()
                : "Sin responsable"}
            </p>

            <hr style={{ margin: "10px 0" }} />

            <div className="form-group-inline">
              <div style={{ flex: 1, marginRight: 8 }}>
                <label>Reasignar a:</label>
                {cargandoUsuariosDepto ? (
                  <p>Cargando usuarios del departamento…</p>
                ) : usuariosDepto.length === 0 ? (
                  <p style={{ fontStyle: "italic" }}>
                    No hay miembros activos en este departamento.
                  </p>
                ) : (
                  <select
                    className="input-layout"
                    value={selectedUsuarioId}
                    onChange={(e) => setSelectedUsuarioId(e.target.value)}
                  >
                    <option value="">Sin responsable</option>
                    {usuariosDepto.map((u) => (
                      <option key={u.id} value={u.id}>
                        {`${u.nombre || ""} ${u.apellidos || ""}`.trim()}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div style={{ flex: 1, marginLeft: 8 }}>
                <label>Estado de la petición:</label>
                <select
                  className="input-layout"
                  value={nuevoEstado}
                  onChange={(e) => setNuevoEstado(e.target.value)}
                >
                  {ESTADOS.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.titulo}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label style={{ marginTop: 12 }}>Comentario (opcional):</label>
            <textarea
              className="input-layout"
              rows={3}
              value={comentarioAccion}
              onChange={(e) => setComentarioAccion(e.target.value)}
              placeholder="Añade contexto sobre el cambio de estado o la reasignación…"
            />

            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                type="button"
                className="btn-secundario"
                onClick={cerrarPanelTarea}
                disabled={guardandoPanel}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-login"
                onClick={guardarPanelTarea}
                disabled={guardandoPanel}
              >
                {guardandoPanel ? "Guardando..." : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableroTareasDepartamento;
